import {match} from 'ts-pattern';

import {useState} from 'react';
import {useDispatch} from 'react-redux';

import {isNil, isNotNil} from 'ramda';

import {documentContextApi} from '@dms/api/core';
import {
  GetCheckoutByIdApiResponse,
  Payment,
  PostCheckoutPaymentIssueApiResponse,
  usePatchCheckoutPaymentFullCorrectionMutation,
  usePatchCheckoutPaymentMutation,
  usePatchCheckoutPaymentPartialCorrectionMutation,
  usePostCheckoutPaymentFullCorrectionIssueMutation,
  usePostCheckoutPaymentInvoiceIssueMutation,
  usePostCheckoutPaymentPartialCorrectionIssueMutation,
} from '@dms/api/sales';
import {
  DEFAULT_FOREIGN_CURRENCY,
  handleApiError,
  printPDF,
  useBank,
  useCheckoutIssuing,
  useTenant,
} from '@dms/shared';

import {getApiDateString, precisionCalculation, useDebouncedCallback} from 'shared/web';

import {CheckoutPaymentFormTypeV2} from '../types/CheckoutPaymentFormTypeV2';

const SAVE_DEBOUNCE_MS = 1000;
const DOCUMENTS_COUNT_INVALIDATION_MS = 1000;

interface UseCheckoutPaymentIssuingParams {
  checkoutId: string;
  paymentId: string;
  payment: Payment;
  checkout: GetCheckoutByIdApiResponse | undefined;
}

export function useCheckoutPaymentIssuing(params: UseCheckoutPaymentIssuingParams) {
  const [isLazySaving, setIsLazySaving] = useState(false);
  const dispatch = useDispatch();

  const {getBankAccountByNumber} = useBank();
  const {tenantCurrency} = useTenant();
  const {setIsCheckoutIssuing} = useCheckoutIssuing();

  const paymentData = params.payment;
  const checkoutData = params.checkout;

  const [patchCheckoutPayment] = usePatchCheckoutPaymentMutation();
  const [postCheckoutPaymentInvoiceIssue, {isLoading: isPostCheckoutPaymentInvoiceIssueLoading}] =
    usePostCheckoutPaymentInvoiceIssueMutation();
  const [patchPartialCorrection, {isLoading: isPatchPartialCorrectionLoading}] =
    usePatchCheckoutPaymentPartialCorrectionMutation();
  const [patchFullCorrection, {isLoading: isPatchFullCorrectionLoading}] =
    usePatchCheckoutPaymentFullCorrectionMutation();
  const [postPartialCorrectionIssue, {isLoading: isPostPartialCorrectionIssueLoading}] =
    usePostCheckoutPaymentPartialCorrectionIssueMutation();
  const [postFullCorrectionIssue, {isLoading: isPostFullCorrectionIssueLoading}] =
    usePostCheckoutPaymentFullCorrectionIssueMutation();

  const isCorrectionIssuing =
    isPatchPartialCorrectionLoading ||
    isPatchFullCorrectionLoading ||
    isPostPartialCorrectionIssueLoading ||
    isPostFullCorrectionIssueLoading;

  const invalidateDocumentsCount = () => {
    setTimeout(() => {
      dispatch(documentContextApi.util.invalidateTags(['documentsCount']));
    }, DOCUMENTS_COUNT_INVALIDATION_MS);
  };

  const persistPaymentChanges = (data: CheckoutPaymentFormTypeV2) => {
    if (
      isNil(checkoutData) ||
      isNil(data.dateOfTaxableSupply) ||
      isNil(data.dueDate) ||
      isNil(data.issueDate)
    ) {
      return undefined;
    }

    const bankAccount = isNotNil(data.bankAccount)
      ? getBankAccountByNumber(data.bankAccount)
      : undefined;

    const foreignCurrencyAmount =
      isNotNil(data.exchangeRate) && data.exchangeRate > 0
        ? parseFloat(precisionCalculation.divide(data.amount ?? 0, data.exchangeRate).toFixed(2))
        : 0;

    return patchCheckoutPayment({
      checkoutId: params.checkoutId,
      paymentId: params.paymentId,
      body: {
        paymentAmount: {
          amount: data.amount,
          currency: paymentData.paymentAmount?.currency ?? checkoutData?.currency ?? tenantCurrency,
        },
        foreignCurrencyPaymentAmount: checkoutData?.isForeignCurrencyPayment
          ? {
              amount: foreignCurrencyAmount,
              currency:
                paymentData.foreignCurrencyPaymentAmount?.currency ?? DEFAULT_FOREIGN_CURRENCY,
            }
          : undefined,
        dateOfTaxableSupply: getApiDateString(data.dateOfTaxableSupply),
        dueDate: getApiDateString(data.dueDate),
        issueDate: getApiDateString(data.issueDate),
        method: data.method,
        customerServiceContractId: data.customerServiceContractId,
        templateId: data.templateId,
        bankAccount: bankAccount
          ? {
              currency: bankAccount.currency ?? checkoutData?.currency ?? tenantCurrency,
              isPrimary: !!bankAccount.isPrimary,
              bankName: bankAccount.bankName,
              iban: bankAccount.iban,
              name: bankAccount.accountName,
              number: bankAccount.accountNumber,
              swift: bankAccount.swift,
            }
          : undefined,
        cashRegisterId: data.cashRegisterId,
        due: data.due,
        exchangeRate: data.exchangeRate ? String(data.exchangeRate) : undefined,
        note: data.note,
      },
    }).unwrap();
  };

  function handleSaveChanges(data: CheckoutPaymentFormTypeV2) {
    const savePromise = persistPaymentChanges(data);

    if (isNil(savePromise)) {
      setIsLazySaving(false);
      return;
    }

    return savePromise.catch(handleApiError).finally(() => setIsLazySaving(false));
  }

  const debouncedSaveChanges = useDebouncedCallback(handleSaveChanges, SAVE_DEBOUNCE_MS);

  const handleFormChange = (data: CheckoutPaymentFormTypeV2) => {
    setIsLazySaving(true);
    return debouncedSaveChanges(data);
  };

  const printIssuedDocument = (res: PostCheckoutPaymentIssueApiResponse) => {
    if (res?.file?.pdfUrl) {
      printPDF(res.file.pdfUrl);
    }
  };

  const buildCorrectionBody = (data: CheckoutPaymentFormTypeV2) => ({
    issueDate: getApiDateString(data.issueDate),
    due: data.due,
    dueDate: getApiDateString(data.dueDate),
    dateOfTaxableSupply: getApiDateString(data.dateOfTaxableSupply),
    correctionDetails:
      isNotNil(data.dateOfReception) && isNotNil(data.correctionReason)
        ? {
            dateOfReception: getApiDateString(data.dateOfReception),
            reason: data.correctionReason,
          }
        : null,
    note: data.note,
    templateId: data.templateId,
  });

  const handleIssueRegularPayment = (data: CheckoutPaymentFormTypeV2) => {
    const savePromise = persistPaymentChanges(data);

    if (isNil(savePromise)) {
      setIsLazySaving(false);
      return;
    }

    return savePromise
      .then(() =>
        postCheckoutPaymentInvoiceIssue({
          checkoutId: params.checkoutId,
          paymentId: params.paymentId,
        }).unwrap()
      )
      .then(printIssuedDocument)
      .then(invalidateDocumentsCount)
      .catch(handleApiError)
      .finally(() => setIsLazySaving(false));
  };

  const handleIssuePartialCorrection = (data: CheckoutPaymentFormTypeV2) =>
    patchPartialCorrection({
      checkoutId: params.checkoutId,
      paymentId: params.paymentId,
      body: buildCorrectionBody(data),
    })
      .unwrap()
      .then(() =>
        postPartialCorrectionIssue({
          checkoutId: params.checkoutId,
          paymentId: params.paymentId,
        }).unwrap()
      )
      .then(printIssuedDocument)
      .then(invalidateDocumentsCount)
      .catch(handleApiError);

  const handleIssueFullCorrection = (data: CheckoutPaymentFormTypeV2) =>
    patchFullCorrection({
      checkoutId: params.checkoutId,
      paymentId: params.paymentId,
      body: buildCorrectionBody(data),
    })
      .unwrap()
      .then(() =>
        postFullCorrectionIssue({
          checkoutId: params.checkoutId,
          paymentId: params.paymentId,
        }).unwrap()
      )
      .then(printIssuedDocument)
      .then(invalidateDocumentsCount)
      .catch(handleApiError);

  const issuePayment = async (data: CheckoutPaymentFormTypeV2) => {
    setIsCheckoutIssuing(true);

    await match(paymentData.discriminator)
      .with('BALANCE_CORRECTION_PARTIAL', () => handleIssuePartialCorrection(data))
      .with('BALANCE_CORRECTION_FULL', () => handleIssueFullCorrection(data))
      .otherwise(() => handleIssueRegularPayment(data));

    setIsCheckoutIssuing(false);
  };

  const isIssuing = isPostCheckoutPaymentInvoiceIssueLoading || isCorrectionIssuing || isLazySaving;

  return {handleFormChange, issuePayment, isLazySaving, isIssuing};
}
